import { createSlice } from '@reduxjs/toolkit';
import axios from '../../../axios/interceptor';
import openNotification from '../../../components/notification';

const initialState = {
    stammdatenList: [],
    mappingList: [],
    exportLogList: [],
    previewData: null,
    loading: false,
    stammdatenLoading: false,
    mappingLoading: false,
    exportLoading: false,
    downloadLoading: false,
    freigabeLoading: false,
    error: null,
    lastExportCharge: null,
};

export const slice = createSlice({
    name: 'datevExport',
    initialState,
    reducers: {
        setLoading: (state, action) => { state.loading = action.payload; },
        setStammdatenLoading: (state, action) => { state.stammdatenLoading = action.payload; },
        setMappingLoading: (state, action) => { state.mappingLoading = action.payload; },
        setExportLoading: (state, action) => { state.exportLoading = action.payload; },
        setDownloadLoading: (state, action) => { state.downloadLoading = action.payload; },
        setFreigabeLoading: (state, action) => { state.freigabeLoading = action.payload; },
        setError: (state, action) => { state.error = action.payload; },
        setStammdatenList: (state, action) => { state.stammdatenList = action.payload; },
        setMappingList: (state, action) => { state.mappingList = action.payload; },
        setExportLogList: (state, action) => { state.exportLogList = action.payload; },
        setPreviewData: (state, action) => { state.previewData = action.payload; },
        setLastExportCharge: (state, action) => { state.lastExportCharge = action.payload; },
    },
});

export const {
    setLoading, setStammdatenLoading, setMappingLoading, setExportLoading,
    setDownloadLoading, setFreigabeLoading, setError,
    setStammdatenList, setMappingList, setExportLogList,
    setPreviewData, setLastExportCharge,
} = slice.actions;

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const getStammdaten = (organizer) => async (dispatch) => {
    dispatch(setStammdatenLoading(true));
    try {
        const res = await axios.get('/datev/stammdaten', { params: { organizer } });
        if (res.data.isSuccess) {
            dispatch(setStammdatenList(res.data.records));
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setStammdatenLoading(false));
    }
};

export const updateStammdaten = (data, organizer) => async (dispatch) => {
    dispatch(setStammdatenLoading(true));
    try {
        const res = await axios.post('/datev/stammdaten/update', data);
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: res.data.message });
            dispatch(getStammdaten(organizer));
        } else {
            openNotification({ type: 'error', message: res.data.message });
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setStammdatenLoading(false));
    }
};

export const getMappings = (organizer) => async (dispatch) => {
    dispatch(setMappingLoading(true));
    try {
        const res = await axios.get('/datev/mapping', { params: { organizer } });
        if (res.data.isSuccess) {
            dispatch(setMappingList(res.data.records));
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setMappingLoading(false));
    }
};

export const upsertMapping = (data, organizer) => async (dispatch) => {
    dispatch(setMappingLoading(true));
    try {
        const res = await axios.post('/datev/mapping', data);
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: res.data.message });
            dispatch(getMappings(organizer));
        } else {
            openNotification({ type: 'error', message: res.data.message });
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setMappingLoading(false));
    }
};

export const deleteMapping = (id, organizer) => async (dispatch) => {
    dispatch(setMappingLoading(true));
    try {
        const res = await axios.post('/datev/mapping/delete', { id });
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: res.data.message });
            dispatch(getMappings(organizer));
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setMappingLoading(false));
    }
};

export const getExportLog = (organizer) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const res = await axios.get('/datev/export-log', { params: { organizer } });
        if (res.data.isSuccess) {
            dispatch(setExportLogList(res.data.records));
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setLoading(false));
    }
};

export const previewExport = (organizer, periodStart, periodEnd) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const res = await axios.get('/datev/preview', { params: { organizer, periodStart, periodEnd } });
        if (res.data.isSuccess) {
            dispatch(setPreviewData(res.data));
        } else {
            openNotification('error', res.data.message);
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setLoading(false));
    }
};

export const runExport = (data) => async (dispatch) => {
    dispatch(setExportLoading(true));
    try {
        const res = await axios.post('/datev/export', data);
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: `Export abgeschlossen. ${res.data.recordCount} Datensätze exportiert.` });
            dispatch(setLastExportCharge(res.data.exportCharge));
            dispatch(getExportLog(data.organizer));
            dispatch(setPreviewData(null));
        } else {
            openNotification({ type: 'error', message: res.data.message });
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setExportLoading(false));
    }
};

export const setFreigabe = (ids, status, organizer, periodStart, periodEnd) => async (dispatch) => {
    dispatch(setFreigabeLoading(true));
    try {
        const res = await axios.post('/datev/freigabe', { ids, status });
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: res.data.message });
            dispatch(previewExport(organizer, periodStart, periodEnd));
        } else {
            openNotification({ type: 'error', message: res.data.message });
        }
    } catch (err) {
        dispatch(setError(err.message));
    } finally {
        dispatch(setFreigabeLoading(false));
    }
};

export const createCorrection = (timeTrackingId, organizer) => async (dispatch) => {
    try {
        const res = await axios.post('/datev/correction', { timeTrackingId, organizer });
        if (res.data.isSuccess) {
            openNotification({ type: 'success', message: res.data.message });
        } else {
            openNotification({ type: 'error', message: res.data.message });
        }
    } catch (err) {
        dispatch(setError(err.message));
    }
};

export default slice.reducer;
